import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { eventsAPI } from '../lib/api';
import { Search, Calendar, MapPin, ExternalLink, X, List, Grid } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format, parseISO, isAfter, isBefore, startOfDay } from 'date-fns';

export const EventsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [eventType, setEventType] = useState(searchParams.get('type') || 'all');
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  const eventTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'meetup', label: 'Meetups' },
    { value: 'conference', label: 'Conferences' },
    { value: 'hackathon', label: 'Hackathons' },
    { value: 'workshop', label: 'Workshops' },
  ];

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const params = {};
        if (search) params.search = search;
        if (eventType && eventType !== 'all') params.event_type = eventType;
        if (selectedDate) {
          params.from_date = format(selectedDate, 'yyyy-MM-dd');
          params.to_date = format(selectedDate, 'yyyy-MM-dd');
        }
        const res = await eventsAPI.getAll(params);
        setEvents(res.data);
      } catch (err) {
        console.error('Error fetching events:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [search, eventType, selectedDate]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (eventType && eventType !== 'all') params.set('type', eventType);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setEventType('all');
    setSelectedDate(null);
    setSearchParams({});
  };

  const hasFilters = search || (eventType && eventType !== 'all') || selectedDate;

  // Get dates with events for calendar highlighting
  const eventDates = events.map(e => startOfDay(parseISO(e.date)));

  // Group events by date for list view
  const groupedEvents = events.reduce((acc, event) => {
    const date = event.date.split('T')[0];
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  return (
    <div data-testid="events-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Events</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Events Calendar</h1>
          <p className="text-muted-foreground max-w-2xl">
            Discover tech events happening in Portland. Meetups, conferences, 
            hackathons, workshops, and more.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border sticky top-16 z-40 bg-background">
        <div className="container-wide py-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center flex-1">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search events..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="events-search"
                />
              </div>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger className="w-[150px]" data-testid="event-type-select">
                  <SelectValue placeholder="Event Type" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" data-testid="search-btn">Search</Button>
              {hasFilters && (
                <Button type="button" variant="ghost" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </form>
            <div className="flex items-center gap-1 border border-border rounded-sm">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                data-testid="view-calendar-btn"
              >
                <Grid className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section>
        <div className="container-wide py-12">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Calendar Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 border border-border p-4 rounded-sm">
                <h3 className="font-semibold mb-4">Select Date</h3>
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-sm"
                  modifiers={{
                    hasEvent: eventDates,
                  }}
                  modifiersStyles={{
                    hasEvent: {
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                    },
                  }}
                  data-testid="events-calendar"
                />
                {selectedDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedDate(null)}
                    className="w-full mt-2"
                  >
                    Clear date
                  </Button>
                )}
              </div>
            </div>

            {/* Events List */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="border border-border p-6 animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                      <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : events.length > 0 ? (
                viewMode === 'list' ? (
                  <div className="space-y-8">
                    {Object.entries(groupedEvents).map(([date, dateEvents]) => (
                      <div key={date}>
                        <h3 className="text-label text-muted-foreground mb-4 sticky top-32 bg-background py-2">
                          {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                        </h3>
                        <div className="space-y-3">
                          {dateEvents.map((event, index) => (
                            <Link
                              key={event.id}
                              to={`/events/${event.id}`}
                              className="block animate-slide-up"
                              style={{ animationDelay: `${index * 50}ms` }}
                              data-testid={`event-card-${event.id}`}
                            >
                              <Card className="card-hover">
                                <CardContent className="p-6">
                                  <div className="flex items-start gap-6">
                                    <div className="text-center min-w-[60px] border-r border-border pr-6">
                                      <div className="text-3xl font-bold">
                                        {format(parseISO(event.date), 'd')}
                                      </div>
                                      <div className="text-xs text-muted-foreground uppercase">
                                        {format(parseISO(event.date), 'MMM')}
                                      </div>
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="badge-category">{event.event_type}</span>
                                        {event.is_virtual && (
                                          <span className="text-xs text-muted-foreground">Virtual</span>
                                        )}
                                        {event.featured && (
                                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                            Featured
                                          </span>
                                        )}
                                      </div>
                                      <h3 className="font-semibold text-lg mb-2">
                                        {event.title}
                                      </h3>
                                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                        {event.description}
                                      </p>
                                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <MapPin className="w-4 h-4" />
                                          {event.location}
                                        </span>
                                        {event.time && (
                                          <span className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            {event.time}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {events.map((event, index) => (
                      <Link
                        key={event.id}
                        to={`/events/${event.id}`}
                        className="group animate-slide-up"
                        style={{ animationDelay: `${index * 50}ms` }}
                        data-testid={`event-grid-${event.id}`}
                      >
                        <Card className="card-hover h-full">
                          <CardContent className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="badge-category">{event.event_type}</span>
                              {event.is_virtual && (
                                <span className="text-xs text-muted-foreground">Virtual</span>
                              )}
                            </div>
                            <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                              {event.title}
                            </h3>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                              {event.description}
                            </p>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                {format(parseISO(event.date), 'MMM d, yyyy')}
                                {event.time && ` at ${event.time}`}
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                {event.location}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                )
              ) : (
                <div className="empty-state">
                  <Calendar className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No events found</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Try adjusting your search or filters
                  </p>
                  <Button variant="outline" onClick={clearFilters}>
                    Clear all filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
